import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import apiClient from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import {
  ArrowLeft,
  Building,
  MapPin,
  Users,
  ShieldCheck,
  ShieldAlert,
  ShieldPlus,
  Scale,
  AlertTriangle,
  CheckCircle2,
  Info,
  FileText,
  AlertCircle,
  BookOpen,
  ChevronDown,
  ChevronRight,
  Calendar,
  Clock,
  Code,
  AlertOctagon,
  GitBranch
} from "lucide-react";
import { PayRuleVisual } from "@/components/payrule";

const PayRuleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rule, setRule] = useState(null);
  const [expandedLaws, setExpandedLaws] = useState({});
  const [showPayRuleJson, setShowPayRuleJson] = useState(false);
  const [showPayRuleVisual, setShowPayRuleVisual] = useState(false);

  const fetchDetail = useCallback(async () => {
    try {
      const response = await apiClient.get(`/api/pay-rules/${encodeURIComponent(id)}`);
      setRule(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load pay rule details");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const toggleLawExpanded = (issueIndex, lawIndex) => {
    const key = `${issueIndex}-${lawIndex}`;
    setExpandedLaws(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Get issue categories for highlighting
  const getIssueCategories = () => {
    const issues = rule?.Issues || [];
    const categories = new Set();
    issues.forEach(issue => {
      if (issue.Category) {
        categories.add(issue.Category.toLowerCase());
      }
    });
    return categories;
  };

  // Check if a key should be highlighted based on issue categories
  const shouldHighlight = (key, issueCategories) => {
    const keyLower = key.toLowerCase();
    
    // Overtime related
    if (issueCategories.has('overtime') && 
        (keyLower.includes('overtime') || keyLower.includes('ot') || keyLower === 'overtimes')) {
      return true;
    }
    
    // Break related
    if ((issueCategories.has('meal break') || issueCategories.has('rest break') || issueCategories.has('break violation')) && 
        (keyLower.includes('break') || keyLower.includes('meal') || keyLower.includes('rest'))) {
      return true;
    }
    
    // Work rule related
    if (keyLower.includes('work_rule') || keyLower === 'work_rule') {
      return true;
    }
    
    return false;
  };

  // Render JSON with highlighting
  const renderJsonValue = (value, key, issueCategories, depth = 0) => {
    const isHighlighted = shouldHighlight(key, issueCategories);
    const baseClass = isHighlighted ? "bg-red-100 border-l-2 border-red-500 pl-2 -ml-2" : "";
    
    if (value === null || value === undefined) {
      return <span className="text-muted-foreground italic">null</span>;
    }
    
    if (typeof value === 'boolean') {
      return <span className={value ? "text-emerald-600" : "text-red-600"}>{value.toString()}</span>;
    }
    
    if (typeof value === 'number') {
      return <span className="text-blue-600">{value}</span>;
    }
    
    if (typeof value === 'string') {
      if (value === '') {
        return <span className="text-muted-foreground italic">empty</span>;
      }
      return <span className="text-emerald-700">"{value}"</span>;
    }
    
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <span className="text-muted-foreground">[]</span>;
      }
      return (
        <div className={`${baseClass} space-y-1`}>
          <span className="text-muted-foreground">[</span>
          <div className="ml-4 space-y-1">
            {value.map((item, index) => (
              <div key={`${key}-${index}-${typeof item === 'object' ? JSON.stringify(item).slice(0, 20) : item}`} className="flex">
                <span className="text-muted-foreground mr-2">{index}:</span>
                {renderJsonValue(item, key, issueCategories, depth + 1)}
              </div>
            ))}
          </div>
          <span className="text-muted-foreground">]</span>
        </div>
      );
    }
    
    if (typeof value === 'object') {
      const entries = Object.entries(value);
      if (entries.length === 0) {
        return <span className="text-muted-foreground">{"{}"}</span>;
      }
      return (
        <div className={`${baseClass} space-y-1`}>
          {entries.map(([k, v]) => {
            const keyHighlighted = shouldHighlight(k, issueCategories);
            return (
              <div key={k} className={`flex flex-wrap ${keyHighlighted ? "bg-red-50 rounded p-1 -m-1" : ""}`}>
                <span className={`font-mono text-sm ${keyHighlighted ? "text-red-700 font-semibold" : "text-primary"}`}>
                  {keyHighlighted && <AlertOctagon className="w-3 h-3 inline mr-1 text-red-500" />}
                  {k}:
                </span>
                <span className="ml-2">{renderJsonValue(v, k, issueCategories, depth + 1)}</span>
              </div>
            );
          })}
        </div>
      );
    }
    
    return <span>{String(value)}</span>;
  };

  // Memoize sorted state employee counts - MUST be before early returns
  const sortedStateEmployees = useMemo(() => {
    const counts = rule?.StateEmployeeCounts || {};
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [rule?.StateEmployeeCounts]);
  
  // Parse Pay Rule JSON - MUST be before early returns
  const payRuleJson = useMemo(() => {
    if (!rule?.PayRuleJSON) return null;
    try {
      return typeof rule.PayRuleJSON === 'string' ? JSON.parse(rule.PayRuleJSON) : rule.PayRuleJSON;
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to parse PayRuleJSON:', e);
      }
      return null;
    }
  }, [rule?.PayRuleJSON]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading details...</p>
        </div>
      </div>
    );
  }

  if (!rule) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Pay rule not found</p>
      </div>
    );
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case "Compliant":
        return <ShieldCheck className="w-6 h-6 text-emerald-600" />;
      case "Non-Compliant":
        // Only show red alert icon if there are Critical issues
        if (rule.CriticalCount > 0) {
          return <ShieldAlert className="w-6 h-6 text-red-600" />;
        }
        return <ShieldAlert className="w-6 h-6 text-orange-500" />; // Orange for High/non-critical
      case "Over-Compliant":
        return <ShieldPlus className="w-6 h-6 text-amber-600" />;
      default:
        return <Info className="w-6 h-6 text-muted-foreground" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "Compliant":
        return <Badge className="status-badge status-compliant">Compliant</Badge>;
      case "Non-Compliant":
        return <Badge className="status-badge status-non-compliant">Non-Compliant</Badge>;
      case "Over-Compliant":
        return <Badge className="status-badge status-over-compliant">Over-Compliant</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getSeverityBadge = (severity) => {
    switch (severity?.toLowerCase()) {
      case "critical":
        return <Badge className="bg-red-700 text-white">Critical</Badge>;
      case "high":
      case "error":
        return <Badge className="bg-orange-500 text-white">High</Badge>;
      case "medium":
        return <Badge className="bg-amber-500 text-white">Medium</Badge>;
      case "warning":
        return <Badge className="bg-yellow-500 text-black">Warning</Badge>;
      case "low":
        return <Badge className="bg-sky-500 text-white">Low</Badge>;
      case "info":
        return <Badge className="bg-slate-500 text-white">Info</Badge>;
      default:
        return <Badge variant="secondary">{severity || "Info"}</Badge>;
    }
  };

  const issues = rule.Issues || [];
  const stateEmployeeCounts = rule.StateEmployeeCounts || {};
  const issueCategories = getIssueCategories();

  return (
    <div className="p-6 space-y-6 animate-fade-in" data-testid="pay-rule-detail">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/dashboard")}
          className="mt-1"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            {getStatusIcon(rule.ComplianceStatus)}
            <h1 className="text-2xl font-bold font-['Manrope'] tracking-tight">
              {rule.PayRuleName}
            </h1>
            {getStatusBadge(rule.ComplianceStatus)}
          </div>
          <div className="flex items-center gap-4 text-muted-foreground flex-wrap">
            <span className="flex items-center gap-1">
              <Building className="w-4 h-4" />
              {rule.CompanyName}
            </span>
            <span className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              {rule.TotalEmployeeCount || 0} total employees
            </span>
            <span className="flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {rule.IssueCount || 0} issues
            </span>
          </div>
        </div>
      </div>

      {/* Summary Alert - Only show for Critical issues */}
      {rule.ComplianceStatus === "Non-Compliant" && rule.CriticalCount > 0 && (
        <Card className="border-red-200 bg-red-50" data-testid="critical-alert">
          <CardContent className="py-4">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-red-900">Critical Issues Detected</h3>
                <p className="text-sm text-red-700 mt-1">
                  This pay rule has {rule.CriticalCount} critical issue{rule.CriticalCount !== 1 ? "s" : ""} that require immediate attention.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {rule.ComplianceStatus === "Over-Compliant" && (
        <Card className="border-amber-200 bg-amber-50" data-testid="over-compliant-alert">
          <CardContent className="py-4">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Info className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-amber-900">Over-Compliant Configuration</h3>
                <p className="text-sm text-amber-700 mt-1">
                  This pay rule has {rule.WarningCount || 0} warning{rule.WarningCount !== 1 ? "s" : ""} - it may exceed requirements in some areas.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {rule.ComplianceStatus === "Compliant" && (
        <Card className="border-emerald-200 bg-emerald-50" data-testid="compliant-alert">
          <CardContent className="py-4">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-emerald-900">Fully Compliant</h3>
                <p className="text-sm text-emerald-700 mt-1">
                  This pay rule meets all requirements with no issues detected.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Employee Count by State */}
      {Object.keys(stateEmployeeCounts).length > 0 && (
        <Card data-testid="employee-by-state">
          <CardHeader>
            <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Employee Count by State
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {sortedStateEmployees.map(([state, count]) => (
                <div key={state} className="p-3 bg-secondary/50 rounded-lg text-center">
                  <p className="text-xs text-muted-foreground">{state}</p>
                  <p className="text-xl font-bold font-['Manrope']">{count}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pay Rule Configuration JSON */}
      {payRuleJson && (
        <Card data-testid="pay-rule-json">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                  <Code className="w-5 h-5 text-primary" />
                  Pay Rule Configuration
                </CardTitle>
                <CardDescription>
                  Full configuration with non-compliant sections highlighted
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowPayRuleVisual(true)}
                  data-testid="view-visual-btn"
                >
                  <GitBranch className="w-4 h-4 mr-1" />
                  View Visual
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowPayRuleJson(!showPayRuleJson)}
                  data-testid="view-config-btn"
                >
                  {showPayRuleJson ? (
                    <>
                      <ChevronDown className="w-4 h-4 mr-1" />
                      Hide Configuration
                    </>
                  ) : (
                    <>
                      <ChevronRight className="w-4 h-4 mr-1" />
                      View Configuration
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardHeader>
          {showPayRuleJson && (
            <CardContent>
              {/* Legend */}
              <div className="flex items-center gap-4 mb-4 p-2 bg-secondary/50 rounded-lg text-xs">
                <span className="flex items-center gap-1">
                  <AlertOctagon className="w-3 h-3 text-red-500" />
                  <span className="text-red-700 bg-red-50 px-1 rounded">Highlighted</span>
                  = Related to compliance issues
                </span>
              </div>
              
              <ScrollArea className="h-[400px] rounded-lg border p-4 bg-secondary/30">
                <div className="font-mono text-sm space-y-1">
                  {renderJsonValue(payRuleJson, 'root', issueCategories)}
                </div>
              </ScrollArea>
            </CardContent>
          )}
        </Card>
      )}

      {/* Issues List with Matched Wage Laws */}
      {issues.length > 0 && (
        <Card data-testid="issues-list">
          <CardHeader>
            <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Issues ({issues.length})
            </CardTitle>
            <CardDescription>Compliance issues with applicable state wage law citations</CardDescription>
          </CardHeader>
          <CardContent>
            <Accordion type="multiple" className="space-y-3">
              {issues.map((issue, issueIndex) => {
                const matchedLaws = issue.matched_wage_laws || [];
                
                return (
                  <AccordionItem 
                    key={issue.IssueID || issueIndex} 
                    value={`issue-${issueIndex}`}
                    className="border rounded-lg px-4"
                  >
                    <AccordionTrigger className="hover:no-underline py-4">
                      <div className="flex items-center gap-3 text-left flex-1">
                        {getSeverityBadge(issue.Severity)}
                        <div className="flex-1">
                          <p className="font-medium text-sm">{issue.Title || "Issue"}</p>
                          <p className="text-xs text-muted-foreground">
                            {issue.State && (
                              <span className="inline-flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {issue.State}
                                {issue.StateEmployeeCount && ` (${issue.StateEmployeeCount} employees)`}
                              </span>
                            )}
                            {issue.Category && ` • ${issue.Category}`}
                            {matchedLaws.length > 0 && (
                              <span className="ml-2 inline-flex items-center gap-1 text-primary">
                                <Scale className="w-3 h-3" />
                                {matchedLaws.length} law{matchedLaws.length !== 1 ? 's' : ''} cited
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-4 pt-2 pb-2">
                        {/* Issue Description */}
                        {issue.Description && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">Description</p>
                            <p className="text-sm">{issue.Description}</p>
                          </div>
                        )}
                        
                        {/* Recommendation */}
                        {issue.Recommendation && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <p className="text-xs font-medium text-blue-700 mb-1">Recommendation</p>
                            <p className="text-sm text-blue-900">{issue.Recommendation}</p>
                          </div>
                        )}
                        
                        {/* AI Analysis */}
                        {issue.ai_reasoning && (
                          <div className="bg-secondary/50 rounded-lg p-3">
                            <p className="text-xs font-medium text-muted-foreground mb-1">AI Analysis</p>
                            <p className="text-sm">{issue.ai_reasoning}</p>
                            {issue.ai_confidence && (
                              <p className="text-xs text-muted-foreground mt-1">Confidence: {issue.ai_confidence}</p>
                            )}
                          </div>
                        )}
                        
                        {/* Matched Wage Laws - Collapsible, hidden by default */}
                        {matchedLaws.length > 0 && (
                          <div className="mt-4">
                            <Separator className="mb-3" />
                            <Collapsible>
                              <CollapsibleTrigger asChild>
                                <button className="flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary/80 transition-colors w-full">
                                  <Scale className="w-4 h-4" />
                                  <span>View Applicable {issue.State} Wage Law{matchedLaws.length !== 1 ? 's' : ''}</span>
                                  <Badge variant="secondary" className="text-xs ml-1">{matchedLaws.length}</Badge>
                                  <ChevronRight className="w-4 h-4 ml-auto transition-transform [[data-state=open]>&]:rotate-90" />
                                </button>
                              </CollapsibleTrigger>
                              <CollapsibleContent>
                                <div className="space-y-3 mt-3">
                              {matchedLaws.map((law, lawIndex) => {
                                const isExpanded = expandedLaws[`${issueIndex}-${lawIndex}`];
                                const hasVerbose = law.StatuteVerbose || law.DamageFormulaDescription || law.Notes;
                                const lawKey = `${law.StateName}-${law.ViolationType}-${lawIndex}`;
                                
                                return (
                                  <div key={lawKey} className="border-l-2 border-primary/30 pl-4 py-2 bg-primary/5 rounded-r-lg">
                                    <div className="flex items-start justify-between gap-2 mb-1">
                                      <div>
                                        <p className="font-medium text-sm text-foreground">{law.ViolationType}</p>
                                        {law.StatuteReference && (
                                          <p className="text-xs text-primary font-mono mt-0.5">
                                            <BookOpen className="w-3 h-3 inline mr-1" />
                                            {law.StatuteReference}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* Summary */}
                                    {law.StatuteSummary && (
                                      <p className="text-sm text-muted-foreground my-2">
                                        {law.StatuteSummary}
                                      </p>
                                    )}
                                    
                                    {/* Expandable Full Details */}
                                    {hasVerbose && (
                                      <Collapsible 
                                        open={isExpanded} 
                                        onOpenChange={() => toggleLawExpanded(issueIndex, lawIndex)}
                                        className="mt-3"
                                      >
                                        <CollapsibleTrigger asChild>
                                          <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            className="w-full justify-start text-xs text-primary hover:text-primary"
                                          >
                                            {isExpanded ? (
                                              <ChevronDown className="w-3 h-3 mr-1" />
                                            ) : (
                                              <ChevronRight className="w-3 h-3 mr-1" />
                                            )}
                                            {isExpanded ? "Hide" : "View"} Full Statute Details
                                          </Button>
                                        </CollapsibleTrigger>
                                        <CollapsibleContent>
                                          <div className="mt-2 p-3 bg-card rounded-lg border space-y-3">
                                            {/* Verbose Statute Text */}
                                            {law.StatuteVerbose && (
                                              <div>
                                                <p className="text-xs font-semibold text-muted-foreground mb-1 flex items-center gap-1">
                                                  <BookOpen className="w-3 h-3" />
                                                  Full Statute Text
                                                </p>
                                                <p className="text-sm text-foreground whitespace-pre-wrap">{law.StatuteVerbose}</p>
                                              </div>
                                            )}
                                            
                                            {/* Damage Formula */}
                                            {law.DamageFormulaDescription && (
                                              <div>
                                                <p className="text-xs font-semibold text-muted-foreground mb-1">Damage Formula</p>
                                                <p className="text-sm font-mono bg-secondary/50 p-2 rounded">{law.DamageFormulaDescription}</p>
                                              </div>
                                            )}
                                            
                                            {/* Additional Details */}
                                            <div className="grid grid-cols-2 gap-3 text-xs">
                                              {law.DamageUnitType && (
                                                <div>
                                                  <span className="text-muted-foreground block">Damage Unit Type</span>
                                                  <span className="font-medium text-foreground">{law.DamageUnitType}</span>
                                                </div>
                                              )}
                                              {law.StatuteOfLimitationsYears && (
                                                <div>
                                                  <span className="text-muted-foreground block flex items-center gap-1">
                                                    <Clock className="w-3 h-3" />
                                                    Statute of Limitations
                                                  </span>
                                                  <span className="font-medium text-foreground">{law.StatuteOfLimitationsYears} years</span>
                                                </div>
                                              )}
                                              {law.EffectiveAsOf && (
                                                <div>
                                                  <span className="text-muted-foreground block flex items-center gap-1">
                                                    <Calendar className="w-3 h-3" />
                                                    Effective Date
                                                  </span>
                                                  <span className="font-medium text-foreground">{law.EffectiveAsOf.split('T')[0]}</span>
                                                </div>
                                              )}
                                              {law.IsApplicable !== undefined && (
                                                <div>
                                                  <span className="text-muted-foreground block">Applicability</span>
                                                  <Badge variant={law.IsApplicable ? "default" : "secondary"} className="text-xs">
                                                    {law.IsApplicable ? "Applicable" : "Not Applicable"}
                                                  </Badge>
                                                </div>
                                              )}
                                            </div>
                                            
                                            {/* Notes */}
                                            {law.Notes && (
                                              <div className="border-t pt-2">
                                                <p className="text-xs font-semibold text-muted-foreground mb-1">Additional Notes</p>
                                                <p className="text-sm text-muted-foreground">{law.Notes}</p>
                                              </div>
                                            )}
                                          </div>
                                        </CollapsibleContent>
                                      </Collapsible>
                                    )}
                                  </div>
                                );
                              })}
                                </div>
                              </CollapsibleContent>
                            </Collapsible>
                          </div>
                        )}
                        
                        {issue.Code && (
                          <p className="text-xs text-muted-foreground mt-2">Code: {issue.Code}</p>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </CardContent>
        </Card>
      )}

      {/* Pay Rule Visual Overlay */}
      <PayRuleVisual
        open={showPayRuleVisual}
        onClose={() => setShowPayRuleVisual(false)}
        payRuleJson={rule?.PayRuleJSON}
        issues={rule?.Issues}
      />
    </div>
  );
};

export default PayRuleDetail;
